// @owners { team: patients-team }
import { ActionSheetContext, LoadingEmptyState, XlPadding } from '@alto/design-system';
import { useQueryLandingPageConfigs } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import React, { useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
// eslint-disable-next-line import/no-deprecated
import { fetchOnboardState, setTempPassword } from '~shared/actions/onboarding';
import { ONBOARD_STATE_DONE } from '~shared/features/onboarding/helpers/onboardingRouteHandler';
import { useAnalytics } from '~shared/hooks/useAnalytics';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { ProgynyLandingPage } from './ProgynyLandingPage';
import { VerifyActionSheet } from '~web/features/onboarding/components/redesign/VerifyActionSheet';
import { PartnershipLandingPage } from '~web/features/web-landing-page-tracker/components/redesign/PartnerLandingPage';

type ProgynyLandingPageRouteParams = {
  readonly tempPassword: string;
};

export const ProgynyLandingPageContainer = () => {
  const { tempPassword = '' } = useParams<ProgynyLandingPageRouteParams>();
  const { navigate } = useNavigation();
  const dispatch = useDispatchShared();
  const { trackEvent, trackPageView } = useAnalytics();
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const { partnershipName, isFetching } = useQueryLandingPageConfigs({ tempPassword });
  const loading = useSelectorShared((state) => state.ui.loading.fetchOnboardStateLoading);

  useEffect(() => {
    trackPageView({
      event: EVENTS.ONBOARDING__WEB_LANDING_PAGE_VIEWED,
      params: {
        is_progyny_rx_view: true,
      },
      additionalFields: {
        userIdentifier: tempPassword,
        userIdentifierType: 'temp_password',
      },
    });
  }, [trackPageView, tempPassword]);

  useEffect(() => {
    dispatch(setTempPassword(tempPassword));
  }, [dispatch, tempPassword]);

  const handleOnboard = async () => {
    trackEvent({
      event: EVENTS.WEB_LANDING_PAGE__GET_STARTED_TAPPED,
      params: {
        is_progyny_rx_view: true,
      },
      additionalFields: {
        userIdentifier: tempPassword,
        userIdentifierType: 'temp_password',
      },
    });

    // eslint-disable-next-line import/no-deprecated
    const result = await dispatch(fetchOnboardState(tempPassword));
    const onboardState = result.onboard_state;
    // If the user has already completed onboarding, redirect to login. Otherwise, show verify modal/drawer
    if (onboardState === ONBOARD_STATE_DONE) {
      navigate('RouteLogin');
    } else {
      setActiveActionSheet(<VerifyActionSheet tempPassword={tempPassword} />);
    }
  };

  if (isFetching) {
    return (
      <XlPadding>
        <LoadingEmptyState />
      </XlPadding>
    );
  }

  if (partnershipName === 'progyny') {
    return (
      <PartnershipLandingPage
        handleGetStarted={handleOnboard}
        getStartedLoading={loading}
        tempPassword={tempPassword}
      />
    );
  }

  // Track this, ideally we'd be able to clean up ProgynyLandingPage but not sure if there's ever a time
  // we hit this and there's not a label on the patient.
  trackEvent({ event: EVENTS.PROGYNY_LANDING_PAGE_WRONG_PARTNERSHIP });
  return (
    <ProgynyLandingPage
      handleOnboard={handleOnboard}
      loading={loading}
    />
  );
};
